import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Button, Select, Slider, Row, Col, Tag, Empty, Spin, Alert,
  message, Space, Segmented, Typography,
} from 'antd';
import {
  SearchOutlined, EnvironmentOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const { Text } = Typography;
const SKILL_OPTIONS = [
  { value: '', label: 'Qualquer nível' },
  { value: 'iniciante', label: 'Iniciante' },
  { value: 'intermediario', label: 'Intermediário' },
  { value: 'avancado', label: 'Avançado' },
];
const DAY_OPTIONS = [
  { value: '', label: 'Qualquer dia' },
  { value: '0', label: 'Domingo' }, { value: '1', label: 'Segunda' },
  { value: '2', label: 'Terça' }, { value: '3', label: 'Quarta' },
  { value: '4', label: 'Quinta' }, { value: '5', label: 'Sexta' },
  { value: '6', label: 'Sábado' },
];
const PRICE_OPTIONS = [
  { value: '', label: 'Qualquer valor' },
  { value: '0', label: 'Só grátis' },
  { value: '30', label: 'Até R$ 30' },
  { value: '50', label: 'Até R$ 50' },
  { value: '100', label: 'Até R$ 100' },
];

const Procurar = () => {
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [teams, setTeams] = useState([]);
  const [reason, setReason] = useState(null);
  const [applying, setApplying] = useState(null);

  const [radius, setRadius] = useState(30);
  const [skill, setSkill] = useState('');
  const [day, setDay] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [onlyOpen, setOnlyOpen] = useState('all');
  const [sortTeams, setSortTeams] = useState('distance');

  const buildQuery = (extra = {}) => {
    const p = new URLSearchParams();
    if (radius) p.set('radius', String(radius));
    if (skill) p.set('skill_level', skill);
    if (day !== '') p.set('day_of_week', day);
    if (maxPrice !== '') p.set('max_price', maxPrice);
    Object.entries(extra).forEach(([k, v]) => v !== undefined && v !== '' && p.set(k, String(v)));
    return p.toString();
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/community/players/nearby-teams?${buildQuery({
        sort: sortTeams, limit: 50, only_with_open_game: onlyOpen === 'open' ? 'true' : '',
      })}`);
      setTeams(r.data.teams || []);
      setReason(r.data.reason || null);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Erro ao buscar');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radius, skill, day, maxPrice, onlyOpen, sortTeams]);

  useEffect(() => { load(); }, [load]);

  const applyToTeam = async (t) => {
    setApplying(`team-${t.id}`);
    try {
      // tenta pegar proximo aberto pra vincular ao jogo
      let gameId = null;
      try {
        const r = await api.get(`/community/games/team/${t.id}/next-open`);
        gameId = r.data?.id || null;
      } catch { /* fica GENERIC */ }
      await api.post('/community/invites/apply-to-team',
        gameId
          ? { team_id: t.id, message: `Quero jogar no ${t.name}`, game_id: gameId, kind: 'GAME' }
          : { team_id: t.id, message: `Quero conhecer o ${t.name}`, kind: 'GENERIC' }
      );
      message.success('Bora! Candidatura mandada.');
      load();
    } catch (e) {
      message.error(e?.response?.data?.message || 'Erro ao candidatar');
    } finally { setApplying(null); }
  };

  const noLocation = reason === 'sem_localizacao';

  const filters = (
    <Card size="small" style={{ marginBottom: 16 }}>
      <Row gutter={[12, 12]}>
        <Col xs={24} md={9}>
          <Text style={{ fontSize: 12, color: '#888' }}>Raio: <b>{radius} km</b></Text>
          <Slider min={5} max={100} step={5} value={radius} onChange={setRadius} />
        </Col>
        <Col xs={12} md={5}>
          <Text style={{ fontSize: 12, color: '#888' }}>Nível</Text>
          <Select style={{ width: '100%' }} value={skill} onChange={setSkill} options={SKILL_OPTIONS} />
        </Col>
        <Col xs={12} md={5}>
          <Text style={{ fontSize: 12, color: '#888' }}>Dia</Text>
          <Select style={{ width: '100%' }} value={day} onChange={setDay} options={DAY_OPTIONS} />
        </Col>
        <Col xs={12} md={5}>
          <Text style={{ fontSize: 12, color: '#888' }}>Valor máx</Text>
          <Select style={{ width: '100%' }} value={maxPrice} onChange={setMaxPrice} options={PRICE_OPTIONS} />
        </Col>
      </Row>
      <div style={{ marginTop: 12, textAlign: 'right' }}>
        <Button size="small" icon={<ReloadOutlined />} onClick={() => {
          setRadius(30); setSkill(''); setDay(''); setMaxPrice(''); setOnlyOpen('all');
        }}>Limpar filtros</Button>
      </div>
    </Card>
  );

  const emptyState = noLocation ? (
    <Alert type="info" showIcon
      message="Falta teu CEP pra achar peladas por perto"
      description="Vai lá em Meu Perfil e coloca teu CEP — depois volta aqui."
      action={<Button type="primary" size="small" onClick={() => nav('/player/profile')}>Colocar meu CEP</Button>}
    />
  ) : (
    <Empty description={
      radius < 100
        ? `Nada por aqui em ${radius} km. Aumenta o raio ou muda os filtros.`
        : `Ninguém pertinho ainda. Volta em uns dias, a comunidade tá crescendo.`
    }>
      {radius < 100 && (
        <Button type="primary" onClick={() => setRadius(Math.min(radius + 20, 100))}>
          Aumentar raio pra {Math.min(radius + 20, 100)} km
        </Button>
      )}
    </Empty>
  );

  const teamCard = (t) => (
    <Card key={t.id} size="small" style={{ marginBottom: 10 }}>
      <Row gutter={12} align="middle">
        <Col flex="auto">
          <div style={{ fontWeight: 700, fontSize: 15 }}>{t.name}</div>
          <Space size={6} wrap style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
            <span><EnvironmentOutlined /> {t.neighborhood || t.court_name || t.city}</span>
            <Tag color="blue">{t.distance_km.toFixed(1)} km</Tag>
            {t.skill_level && <Tag>{t.skill_level}</Tag>}
            {t.open_games_week > 0
              ? <Tag color="green">{t.open_games_week} jogo{t.open_games_week > 1 ? 's' : ''} essa semana</Tag>
              : <Tag>sem jogo aberto agora</Tag>}
            {t.guest_price ? <Tag color="gold">R$ {t.guest_price}</Tag> : null}
            {t.average_rating > 0 && <Tag color="purple">⭐ {parseFloat(t.average_rating).toFixed(1)}</Tag>}
          </Space>
        </Col>
        <Col>
          <Button type="primary" loading={applying === `team-${t.id}`} onClick={() => applyToTeam(t)}>
            {t.open_games_week > 0 ? 'Bora!' : 'Ficar de olho'}
          </Button>
        </Col>
      </Row>
    </Card>
  );

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 16 }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 22, fontWeight: 800 }}><SearchOutlined /> Procurar times</div>
        <Text type="secondary">Times boleiros perto de você — pede pra jogar direto pelo painel.</Text>
      </div>

      {filters}

      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Segmented
          size="small"
          value={onlyOpen} onChange={setOnlyOpen}
          options={[
            { label: 'Todos', value: 'all' },
            { label: 'Só com jogo aberto', value: 'open' },
          ]}
        />
        <Segmented
          size="small"
          value={sortTeams} onChange={setSortTeams}
          options={[
            { label: 'Mais perto', value: 'distance' },
            { label: 'Melhor avaliado', value: 'rating' },
          ]}
        />
      </div>
      {loading ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        : teams.length === 0 ? emptyState
        : teams.map(teamCard)}
    </div>
  );
};

export default Procurar;
